/**
 * ChainContext - Blockchain network selection
 * Persists selected chain in localStorage
 */

import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { CHAINS, DEFAULT_CHAIN_ID, SUPPORTED_CHAIN_IDS } from '../constants/chains.js';

const STORAGE_KEY = 'atlas_selectedChainId';

function loadChainId() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? parseInt(stored, 10) : null;
    if (parsed && SUPPORTED_CHAIN_IDS.includes(parsed)) return parsed;
  } catch {}
  return DEFAULT_CHAIN_ID;
}

const ChainContext = createContext(null);

export function ChainProvider({ children }) {
  const [selectedChainId, setSelectedChainId] = useState(loadChainId);

  const switchChain = useCallback((chainId) => {
    const id = parseInt(chainId, 10);
    if (!SUPPORTED_CHAIN_IDS.includes(id)) return;
    setSelectedChainId(id);
    try { localStorage.setItem(STORAGE_KEY, String(id)); } catch {}
  }, []);

  const chainConfig = useMemo(() => CHAINS[selectedChainId], [selectedChainId]);

  const value = useMemo(() => ({
    selectedChainId,
    switchChain,
    chainConfig,
  }), [selectedChainId, switchChain, chainConfig]);

  return (
    <ChainContext.Provider value={value}>
      {children}
    </ChainContext.Provider>
  );
}

export function useChain() {
  const ctx = useContext(ChainContext);
  if (!ctx) throw new Error('useChain must be used within ChainProvider');
  return ctx;
}
